import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
 
 
import { FaRegEdit, FaRegTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import ShopDialog from "./ShopDialog";
import { API_URL } from "../../../../api";
import { 
  CircularProgress, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Box,
  Button
} from "@mui/material";

const getApi = () => {
  const token = localStorage.getItem("token");
  return axios.create({
    baseURL: API_URL(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

const Shop = () => {
  const api = getApi();

  const [shops, setShops] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [maxShops, setMaxShops] = useState(1);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getApi().get("admin-setting");
        const list = res.data.data;
        if (list && list.length > 0) {
          setMaxShops(list[0].maximunShop ?? 1);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error("Failed to fetch admin settings:", err);
        }
      } finally {
        setSettingsLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  const hasShop = settingsLoaded && totalCount >= maxShops;

  const showDialog = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (data) => {
    setEditData(data);
    setOpen(true);
  };

  const handleDelete = (id) => {
    api
      .delete("v2/shop/" + id)
      .then(() => {
        Swal.fire({
          title: "Success",
          text: "Successfully Deleted!",
          icon: "success",
          confirmButtonText: "OK",
        });
        getShops();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || "Error deleting item",
          icon: "error",
          confirmButtonText: "CANCEL",
        });
      });
  };

  const confirmDelete = (id) => {
    Swal.fire({
      title: "Delete Confirmation",
      text: "Are you sure to delete this shop?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(id);
      }
    });
  };

  const getShops = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`v2/shop`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm
        }
      });
      const dataList = res.data?.data || [];
      setShops(dataList);
      setTotalCount(res.data?._metadata?.total || dataList.length);
    } catch (err) {
      // Swal.fire("Error", err.message, "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getShops();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    if (!isLoading && shops.length === 0 && totalCount === 0) {
      setOpen(true);
    }
  }, [isLoading, shops, totalCount]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div className="content-header pb-2">
          <div className="container-fluid px-3">
            <Typography variant="h5" fontWeight="bold" sx={{ color: "#333", display: "flex", alignItems: "center", gap: 1 }}>
              Shop Management
            </Typography>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid px-3">
            <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search shops..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    sx={{ width: { xs: '100%', sm: '300px' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FaPlus />}
                    onClick={showDialog}
                    disabled={hasShop}
                    sx={{ borderRadius: "8px", textTransform: 'none', px: 3 }}
                  >
                    {hasShop ? "Shop Limit Reached" : "Add New Shop"}
                  </Button>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", color: "#333", fontWeight: "bold" } }}>
                        <TableCell>Logo</TableCell>
                        <TableCell>Shop Name</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Branches</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: "center", py: 5 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : shops.length > 0 ? (
                        shops.map((row) => (
                          <TableRow key={row._id || row.id} hover>
                            <TableCell>
                              {row.logo ? (
                                <img 
                                  src={row.logo} 
                                  alt={row.name} 
                                  style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "1px solid #eee" }} 
                                />
                              ) : (
                                <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "#eee", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Typography variant="caption" color="text.secondary">N/A</Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell fontWeight="medium">{row.name}</TableCell>
                            <TableCell>{row.phone}</TableCell>
                            <TableCell sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {row.address}
                            </TableCell>
                            <TableCell>{row.branches?.length || 0} Branches</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit">
                                <IconButton color="primary" onClick={() => handleEdit(row)} size="small">
                                  <FaRegEdit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => confirmDelete(row._id || row.id)} size="small">
                                  <FaRegTrashAlt />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
                            No shops found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      "& p": {
                        marginBottom: 0,
                      },
                    }}
                  />
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        </section>

        <ShopDialog
          open={open}
          close={() => setOpen(false)}
          getShops={getShops}
          editData={editData}
          hasShop={hasShop}
        />
      </div>
    </div>
  );
};

export default Shop;
